/**
 * MinistrareIA - Agent Enhancer Service
 * Usa Azure OpenAI GPT-4.1 para aprimorar as respostas do onboarding
 * 
 * - Recebe o JSON do onboarding
 * - Aprimora cada seção (exceto FAQ que passa direto)
 * - Retorna JSON estruturado + nome sugerido para o agente
 */

// ============================================
// AZURE CONFIG
// ============================================

const AZURE_CONFIG = {
  apiKey: "8v2P2eZPzuRBdF4Hc0UnetnE8rrIO9qWq8jKoKj4UX2FIXZNAko0JQQJ99BGACZoyfiXJ3w3AAABACOGlDBj",
  endpoint: "https://mycarbroker.openai.azure.com/",
  apiVersion: "2024-12-01-preview",
  model: "gpt-4.1"
};

// ============================================
// JSON SCHEMA para Structured Output
// ============================================

const ENHANCED_AGENT_SCHEMA = {
  name: "EnhancedAgentData",
  strict: true,
  schema: {
    type: "object",
    properties: {
      agent_name: {
        type: "string",
        description: "Nome sugerido para o agente (máximo 2 palavras, memorável e profissional)"
      },
      sections: {
        type: "object",
        properties: {
          identity: {
            type: "object",
            properties: {
              original: { type: "string" },
              enhanced: { type: "string" }
            },
            required: ["original", "enhanced"],
            additionalProperties: false
          },
          tone_of_voice: {
            type: "object",
            properties: {
              selected: { type: "string" },
              description: { type: "string" }
            },
            required: ["selected", "description"],
            additionalProperties: false
          },
          objective: {
            type: "object",
            properties: {
              selected: { type: "string" },
              description: { type: "string" }
            },
            required: ["selected", "description"],
            additionalProperties: false
          },
          objective_details: {
            type: "object",
            properties: {
              enhanced_context: { type: "string" }
            },
            required: ["enhanced_context"],
            additionalProperties: false
          },
          rules: {
            type: "object",
            properties: {
              selected: {
                type: "array",
                items: { type: "string" }
              },
              enhanced_rules: { type: "string" }
            },
            required: ["selected", "enhanced_rules"],
            additionalProperties: false
          },
          human_handoff: {
            type: "object",
            properties: {
              original: { type: "string" },
              enhanced: { type: "string" }
            },
            required: ["original", "enhanced"],
            additionalProperties: false
          },
          customization: {
            type: "object",
            properties: {
              original: { type: "string" },
              enhanced: { type: "string" }
            },
            required: ["original", "enhanced"],
            additionalProperties: false
          }
        },
        required: ["identity", "tone_of_voice", "objective", "objective_details", "rules", "human_handoff", "customization"],
        additionalProperties: false
      }
    },
    required: ["agent_name", "sections"],
    additionalProperties: false
  }
};

// ============================================
// SYSTEM PROMPT
// ============================================

const createSystemPrompt = (businessCategory) => {
  const categoryNames = {
    real_estate: 'imobiliária',
    automotive: 'automotivo',
    products: 'e-commerce/varejo',
    support: 'atendimento ao cliente'
  };

  const categoryName = categoryNames[businessCategory] || businessCategory;

  return `Você é um especialista em criar instruções CONCISAS e ACIONÁVEIS para agentes de IA.

CONTEXTO: O usuário está criando um agente para o segmento ${categoryName}.

SUA TAREFA: Aprimorar as respostas do usuário em instruções claras e diretas.

REGRAS CRÍTICAS:
- MÁXIMO 3-4 linhas por seção (exceto identity que pode ter até 6)
- NÃO invente informações que o usuário não disse
- NÃO repita o que já foi dito com mais palavras
- FOCO em instruções acionáveis, não explicações
- Use linguagem direta e objetiva

FORMATO DE CADA SEÇÃO:

1. **identity.enhanced**: 
   - Reescreva a descrição do negócio de forma clara e profissional
   - Máximo 4-6 linhas
   - Mantenha APENAS o que o usuário informou, sem inventar

2. **tone_of_voice.description**:
   - Uma frase definindo o tom + 2 exemplos de frases que o agente deve usar
   - Formato: "Comunique-se de forma [tom]. Exemplos: '[frase1]', '[frase2]'"
   - Máximo 3 linhas

3. **objective.description**:
   - O que o agente deve fazer em UMA frase
   - Formato: "Seu foco é [objetivo]. [Como medir sucesso em 1 frase]"
   - Máximo 2 linhas

4. **objective_details.enhanced_context**:
   - Transforme as respostas do usuário em bullets acionáveis
   - Formato: "• [ponto 1] • [ponto 2] • [ponto 3]"
   - Apenas organize, NÃO adicione informações novas
   - Máximo 4 linhas

5. **rules.enhanced_rules**:
   - Liste as regras como instruções diretas
   - Formato: "• Não [ação proibida] • Não [ação proibida]"
   - Adicione APENAS: "Em dúvidas, redirecione para os benefícios do produto/serviço."
   - Máximo 3 linhas

6. **human_handoff.enhanced**:
   - Transforme em lista de situações claras para escalonar
   - Formato: "Transfira para atendente humano quando: • [situação 1] • [situação 2]"
   - Se vazio, retorne string vazia
   - Máximo 3 linhas

7. **customization.enhanced**:
   - Se vazio, retorne string vazia
   - Se preenchido, mantenha conciso

8. **agent_name**:
   - MÁXIMO 2 palavras
   - Use o nome da empresa/negócio + palavra relacionada ao objetivo
   - Exemplos: "Paware Pro", "Auto Max", "Loja Express"
   - Se não conseguir extrair nome, use: "[Categoria] AI"

EXEMPLO DE OUTPUT BOM:

identity.enhanced: "Paware Softwares desenvolve agentes de IA para automação de atendimento. A plataforma conecta via QR Code e WhatsApp Business OAuth, sem necessidade de migração para cloud API."

tone_of_voice.description: "Comunique-se de forma persuasiva e confiante, destacando benefícios práticos. Exemplos: 'Nossa solução simplifica seu atendimento em minutos.', 'Que tal ver uma demonstração rápida?'"

objective.description: "Seu foco é converter leads em clientes. Sucesso = demonstrações agendadas e interesse qualificado."

objective_details.enhanced_context: "• Destaque a integração fácil via QR Code e WhatsApp OAuth • Enfatize que não precisa migrar para cloud API • Foque na praticidade e rapidez de implementação"

rules.enhanced_rules: "• Não informe preços • Não faça promessas de resultados • Não mencione concorrentes. Em dúvidas, redirecione para os benefícios da plataforma."

human_handoff.enhanced: "Transfira para atendente humano quando: • Cliente pedir desconto ou negociação de preço • Reclamação ou insatisfação • Pedido de cancelamento • Dúvida técnica complexa que você não sabe responder"

LEMBRE-SE: Menos é mais. Instruções curtas e claras são mais eficazes que textos longos.`;
};

// ============================================
// MAIN FUNCTION
// ============================================

/**
 * Aprimora os dados do onboarding usando Azure OpenAI
 * @param {Object} onboardingData - Dados completos do onboarding
 * @returns {Promise<Object>} - Dados aprimorados + nome sugerido
 */
export const enhanceOnboardingData = async (onboardingData) => {
  const { business_category, sections } = onboardingData;

  // Preparar o conteúdo do usuário
  const userContent = prepareUserContent(sections);

  const url = `${AZURE_CONFIG.endpoint}openai/deployments/${AZURE_CONFIG.model}/chat/completions?api-version=${AZURE_CONFIG.apiVersion}`;

  const requestBody = {
    messages: [
      {
        role: "system",
        content: createSystemPrompt(business_category)
      },
      {
        role: "user",
        content: userContent
      }
    ],
    temperature: 0.7,
    max_tokens: 3500,
    response_format: {
      type: "json_schema",
      json_schema: ENHANCED_AGENT_SCHEMA
    }
  };

  console.log('🚀 Enviando para Azure OpenAI...');
  console.log('📤 Request:', JSON.stringify(requestBody, null, 2));

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'api-key': AZURE_CONFIG.apiKey
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Azure OpenAI Error:', response.status, errorText);
      throw new Error(`Azure OpenAI error: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    console.log('📥 Response:', JSON.stringify(data, null, 2));

    // Extrair o conteúdo JSON da resposta
    const content = data.choices?.[0]?.message?.content;
    
    if (!content) {
      throw new Error('Resposta vazia do modelo');
    }

    // Parse do JSON retornado
    const enhancedData = JSON.parse(content);

    // Mesclar com FAQ original (não aprimorado)
    const finalResult = {
      ...enhancedData,
      faq: sections.faq || { items: [] }
    };

    console.log('✅ Dados aprimorados:', JSON.stringify(finalResult, null, 2));

    return finalResult;

  } catch (error) {
    console.error('❌ Erro ao aprimorar dados:', error);
    
    // Fallback: retornar dados originais com nome genérico
    return createFallbackResponse(onboardingData);
  }
};

// ============================================
// HELPERS
// ============================================

/**
 * Prepara o conteúdo do usuário para enviar ao modelo
 */
const prepareUserContent = (sections) => {
  const parts = [];

  // Identity
  if (sections.identity?.value) {
    parts.push(`## Identidade do Negócio\n${sections.identity.value}`);
  }

  // Tone of Voice
  if (sections.tone_of_voice?.selected) {
    const toneNames = {
      formal: 'Formal e Profissional',
      friendly: 'Amigável e Acessível',
      persuasive: 'Persuasivo e Vendedor',
      consultive: 'Consultivo e Educativo'
    };
    parts.push(`## Tom de Voz Selecionado\n${toneNames[sections.tone_of_voice.selected] || sections.tone_of_voice.selected}`);
    if (sections.tone_of_voice.custom) {
      parts.push(`Personalização: ${sections.tone_of_voice.custom}`);
    }
  }

  // Objective
  if (sections.objective?.selected) {
    const objectiveNames = {
      sales: 'Vender',
      qualify: 'Qualificar Leads',
      support: 'Atender/Suportar',
      assistant: 'Assistente do Dia a Dia'
    };
    parts.push(`## Objetivo Principal\n${objectiveNames[sections.objective.selected] || sections.objective.selected}`);
  }

  // Objective Details
  if (sections.objective_details) {
    const details = Object.entries(sections.objective_details)
      .filter(([_, value]) => value && value.trim())
      .map(([key, value]) => `- ${value}`)
      .join('\n');
    
    if (details) {
      parts.push(`## Detalhes do Objetivo\n${details}`);
    }
  }

  // Rules
  if (sections.rules?.selected?.length > 0 || sections.rules?.additional) {
    const ruleNames = {
      no_prices: 'Não informar preços',
      no_competitors: 'Não falar de concorrentes',
      no_promises: 'Não fazer promessas',
      business_hours: 'Respeitar horário comercial'
    };
    
    const selectedRules = sections.rules.selected
      ?.map(r => ruleNames[r] || r)
      .join(', ') || '';
    
    parts.push(`## Regras Selecionadas\n${selectedRules}`);
    
    if (sections.rules.additional) {
      parts.push(`Regras adicionais: ${sections.rules.additional}`);
    }
  }

  // Customization
  if (sections.customization?.value) {
    parts.push(`## Personalização Extra\n${sections.customization.value}`);
  }

  // Human Handoff
  if (sections.human_handoff?.value) {
    parts.push(`## Quando Chamar Humano\n${sections.human_handoff.value}`);
  }

  return parts.join('\n\n') || 'Nenhuma informação fornecida.';
};

/**
 * Cria resposta de fallback em caso de erro
 */
const createFallbackResponse = (onboardingData) => {
  const { business_category, sections } = onboardingData;
  
  const categoryNames = {
    real_estate: 'Imob',
    automotive: 'Auto',
    products: 'Loja',
    support: 'Suporte'
  };

  // Tentar extrair nome do negócio da identidade
  const identityValue = sections.identity?.value || '';
  const extractedName = identityValue.match(/(?:somos? (?:a|o)|chamamos?|empresa)\s+([A-Za-zÀ-ú]+)/i)?.[1];

  const agentName = extractedName 
    ? `${extractedName} AI`
    : `${categoryNames[business_category] || 'Meu'} Agente`;

  return {
    agent_name: agentName.substring(0, 30), // Limitar tamanho
    sections: {
      identity: {
        original: sections.identity?.value || '',
        enhanced: sections.identity?.value || ''
      },
      tone_of_voice: {
        selected: sections.tone_of_voice?.selected || 'friendly',
        description: 'Comunicação natural e acessível.'
      },
      objective: {
        selected: sections.objective?.selected || 'assistant',
        description: 'Auxiliar clientes de forma eficiente.'
      },
      objective_details: {
        enhanced_context: Object.values(sections.objective_details || {}).filter(Boolean).join(' ')
      },
      rules: {
        selected: sections.rules?.selected || [],
        enhanced_rules: sections.rules?.additional || ''
      },
      human_handoff: {
        original: sections.human_handoff?.value || '',
        enhanced: sections.human_handoff?.value || ''
      },
      customization: {
        original: sections.customization?.value || '',
        enhanced: sections.customization?.value || ''
      }
    },
    faq: sections.faq || { items: [] }
  };
};

/**
 * Constrói o prompt final a partir dos dados aprimorados
 */
export const buildEnhancedPrompt = (enhancedData, businessCategory) => {
  const { sections, faq } = enhancedData;
  
  const categoryNames = {
    real_estate: 'imobiliária',
    automotive: 'automotivo',
    products: 'e-commerce',
    support: 'atendimento ao cliente'
  };

  let prompt = `Você é um assistente virtual especializado no segmento ${categoryNames[businessCategory] || businessCategory}.\n\n`;

  // Identidade aprimorada
  if (sections.identity?.enhanced) {
    prompt += `## Sobre o Negócio\n${sections.identity.enhanced}\n\n`;
  }

  // Tom de voz
  if (sections.tone_of_voice?.description) {
    prompt += `## Tom de Comunicação\n${sections.tone_of_voice.description}\n\n`;
  }

  // Objetivo
  if (sections.objective?.description) {
    prompt += `## Objetivo Principal\n${sections.objective.description}\n\n`;
  }

  // Detalhes do objetivo
  if (sections.objective_details?.enhanced_context) {
    prompt += `## Contexto Operacional\n${sections.objective_details.enhanced_context}\n\n`;
  }

  // Regras
  if (sections.rules?.enhanced_rules) {
    prompt += `## Regras e Limites\n${sections.rules.enhanced_rules}\n\n`;
  }

  // FAQ (não aprimorado)
  if (faq?.items?.length > 0) {
    prompt += `## Perguntas Frequentes\n`;
    faq.items.forEach((item, index) => {
      prompt += `${index + 1}. P: ${item.question}\n   R: ${item.answer}\n`;
    });
    prompt += '\n';
  }

  // Escalonamento Humano
  if (sections.human_handoff?.enhanced) {
    prompt += `## Quando Chamar Atendente Humano\n${sections.human_handoff.enhanced}\n\n`;
  }

  // Personalização
  if (sections.customization?.enhanced) {
    prompt += `## Instruções Adicionais\n${sections.customization.enhanced}\n`;
  }

  return prompt;
};

export default {
  enhanceOnboardingData,
  buildEnhancedPrompt
};